'use client';

import { CalendarDays, X } from 'lucide-react';
import { useState } from 'react';
import { useTimePrefs } from '@/hooks/useTimePrefs';
import { startOfLocalDay } from '@/lib/dates';
import { formatDateShort, formatTime } from '@/lib/time-format';
import { cn } from '@/lib/utils';
import { Calendar } from './Calendar';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { TimePicker } from './TimePicker';

interface DateTimePickerProps {
  /** ISO 8601 string with timezone (what the backend stores). Empty for unset. */
  value: string | null;
  onChange: (nextIso: string | null) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
}

// One trigger, one popover. Inside: calendar on top, time row below.
// Keeping both controls inside the same popover preserves the "pick a
// moment" mental model rather than splitting it into two inputs.
export function DateTimePicker({
  value,
  onChange,
  placeholder = 'Pick date & time',
  allowClear = true,
  disabled,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const timePrefs = useTimePrefs();
  const current = value ? new Date(value) : null;
  const time = current ? toTimeOfDay(current) : '09:00';

  function commit(date: Date | null, hhmm: string) {
    if (!date) {
      onChange(null);
      return;
    }
    const [h, m] = hhmm.split(':').map(Number);
    const out = new Date(date);
    out.setHours(h ?? 0, m ?? 0, 0, 0);
    onChange(out.toISOString());
  }

  return (
    <div className="flex items-stretch gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'inline-flex flex-1 items-center justify-between gap-3 rounded-lg border border-border bg-surface-sunken/60 px-3 py-2 text-sm outline-none transition duration-fast ease-out-quint hover:border-border-strong focus-visible:border-accent disabled:cursor-not-allowed disabled:opacity-50',
              !current && 'text-subtle-foreground',
            )}
          >
            {current ? (
              <span className="font-mono tabular-nums">
                {formatDateShort(current)} · {formatTime(current, timePrefs)}
              </span>
            ) : (
              <span>{placeholder}</span>
            )}
            <CalendarDays className="h-3.5 w-3.5 text-subtle-foreground" strokeWidth={2} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="space-y-4 p-4">
          <Calendar
            value={current ? startOfLocalDay(current) : null}
            onChange={(d) => commit(d, time)}
          />
          <div className="hairline" />
          <div className="flex items-center justify-between">
            <p className="eyebrow">Time</p>
            <TimePicker
              value={time}
              onChange={(t) => commit(current ?? startOfLocalDay(new Date()), t)}
            />
          </div>
        </PopoverContent>
      </Popover>
      {allowClear && value && !disabled && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label="Clear scheduled time"
          className="rounded-lg border border-border px-2 text-muted-foreground transition duration-fast ease-out-quint hover:bg-surface-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function toTimeOfDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
